import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  GetInspectionTop,
  PutInspectionTop,
  Status,
} from '../../services/apiTypes';
import {
  fetchInspectionTop as fetchInspectionTopAPI,
  InspectionTopParams,
  updateInspectionTop as updateInspectionTopAPI,
  updateQcTop as updateQcTopAPI,
  fetchQcTop as fetchQcTopAPI,
} from './api';
import { isGetInspectionTop } from '../../_utils/typeGuardUtils';

interface InspectionTopState {
  status: Status;
  inspectionTop: GetInspectionTop;
}

const initialInspectionTop: GetInspectionTop = {
  rmaNo: '',
  inspectorName: '',
  selectedInspectionTypeId: -1,
  inspectionTypeList: [],
  selectedCategoryId: '',
  partsCategoryList: [],
  inspectionCategoryList: [],
  qcCount: 0,
  isSerialChecked: false,
};

const initialState: InspectionTopState = {
  status: 'idle',
  inspectionTop: initialInspectionTop,
};

const getToPutData = (data: GetInspectionTop): PutInspectionTop => {
  return {
    rmaNo: data.rmaNo,
    inspectorName: data.inspectorName,
    selectedInspectionTypeId: data.selectedInspectionTypeId ?? -1,
    selectedCategoryId: data.selectedCategoryId,
    inspectionCategoryList: data.inspectionCategoryList,
    isSerialChecked: data.isSerialChecked,
  };
};

export const fetchInspectionTop = createAsyncThunk(
  'inspectionTop/fetchInspectionTop',
  async (
    params: { query: InspectionTopParams; isInspection: boolean },
    { rejectWithValue },
  ) => {
    try {
      if (params.isInspection) {
        return await fetchInspectionTopAPI(params.query);
      } else {
        return await fetchQcTopAPI(params.query);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateInspectionTop = createAsyncThunk(
  'inspectionTop/updateInspectionTop',
  async (
    params: {
      newInspectionTop: GetInspectionTop;
      isInspection: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const newInspectionTop = getToPutData(params.newInspectionTop);
      if (params.isInspection) {
        return await updateInspectionTopAPI(newInspectionTop);
      } else {
        return await updateQcTopAPI(newInspectionTop);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const inspectionTopSlice = createSlice({
  name: 'inspectionTop',
  initialState,
  reducers: {
    setInspector: (state, action: PayloadAction<string>) => {
      if (state.inspectionTop) {
        state.inspectionTop.inspectorName = action.payload;
      }
    },
    setSelectedInspectionTypeId: (state, action: PayloadAction<number>) => {
      if (state.inspectionTop) {
        state.inspectionTop.selectedInspectionTypeId = action.payload;
      }
    },
    setSelectedCategoryId: (state, action: PayloadAction<string>) => {
      if (state.inspectionTop) {
        state.inspectionTop.selectedCategoryId = action.payload;
      }
    },
    updateCategoryProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>,
    ) => {
      const { id, progress } = action.payload;
      const newCategoryList = state.inspectionTop.inspectionCategoryList.map(
        (category) => {
          if (category.id === id) {
            return { ...category, progress };
          }
          return category;
        },
      );
      state.inspectionTop.inspectionCategoryList = newCategoryList;
    },
    resetInspectionTop: (state) => {
      state.inspectionTop = initialInspectionTop;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInspectionTop.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchInspectionTop.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetInspectionTop(action.payload)) {
          state.inspectionTop = action.payload;
        }
      })
      .addCase(fetchInspectionTop.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updateInspectionTop.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateInspectionTop.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.inspectionTop = action.meta.arg.newInspectionTop;
      })
      .addCase(updateInspectionTop.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const {
  setInspector,
  setSelectedInspectionTypeId,
  setSelectedCategoryId,
  updateCategoryProgress,
  resetInspectionTop,
} = inspectionTopSlice.actions;

export default inspectionTopSlice.reducer;
